import React from "react";
import styled from "styled-components";

import MuiSnackbar from "@material-ui/core/Snackbar";

const STYLES = {
  SUCCESS: "SUCCESS",
  WARNING: "WARNING",
  ERROR: "ERROR",
  INFO: "INFO",
} as const;

type StyleKey = keyof typeof STYLES;

const surfaceFor: Record<StyleKey, string> = {
  SUCCESS: "var(--color-success-subtle)",
  ERROR: "var(--color-danger-subtle)",
  WARNING: "var(--color-warning-subtle)",
  INFO: "var(--color-info-subtle)",
};

const accentFor: Record<StyleKey, string> = {
  SUCCESS: "var(--color-success)",
  ERROR: "var(--color-danger)",
  WARNING: "var(--color-warning)",
  INFO: "var(--color-info)",
};

const Panel = styled.div<{ $type: StyleKey }>`
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  min-width: 280px;
  max-width: 440px;
  padding: var(--space-3) var(--space-4);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-left: 3px solid ${(p) => accentFor[p.$type]};
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  font-family: var(--font-sans);
`;

const IconCircle = styled.span<{ $type: StyleKey }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => surfaceFor[p.$type]};
  color: ${(p) => accentFor[p.$type]};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text);
  line-height: var(--line-height-normal);
  padding-top: 2px;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--color-text-tertiary);
  cursor: pointer;
  flex-shrink: 0;
  transition: background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-subtle);
    color: var(--color-text);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const InfoIconSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const renderIconFor = (type: StyleKey) => {
  switch (type) {
    case "SUCCESS":
      return <CheckIcon />;
    case "WARNING":
      return <AlertIcon />;
    case "ERROR":
      return <ErrorIcon />;
    case "INFO":
    default:
      return <InfoIconSvg />;
  }
};

const getConfiguration = (
  message: any,
  error: any
): { text: string; type: StyleKey | null } => {
  if (error) {
    return { type: "ERROR", text: typeof error === "string" ? error : String(error) };
  }
  if (message) {
    return { type: "SUCCESS", text: message };
  }
  return { type: null, text: "" };
};

type Props = {
  onCloseSnackbar: any;
  message: string;
  error: boolean | any;
  isOpen: boolean;
};

const CustomSnackbar = ({
  onCloseSnackbar,
  message,
  isOpen,
  error,
}: Props): any => {
  const { text, type } = getConfiguration(message, error);

  return (
    <MuiSnackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      onClose={onCloseSnackbar}
      autoHideDuration={3500}
      open={isOpen}
    >
      {type && text ? (
        <Panel className="ds-root" $type={type}>
          <IconCircle $type={type}>{renderIconFor(type)}</IconCircle>
          <Body>{text}</Body>
          <CloseButton
            type="button"
            onClick={onCloseSnackbar}
            aria-label="Close"
          >
            <CloseIcon />
          </CloseButton>
        </Panel>
      ) : (
        <span />
      )}
    </MuiSnackbar>
  );
};

export default CustomSnackbar;
